// =========================================================================
// Nav badge counts — the real figures behind the sidebar pills. Fork-owned.
//
// P2 shipped `oem-nav.ts` with literal `badge:` numbers and a
// `TODO(p1-merge)` saying to replace them once the engine and the seeds
// landed. This module is that replacement, and the shape of it is dictated by
// one constraint the TODO could not have known:
//
//   **The sidebar renders on first paint. Most OEM data is lazy.**
//
// A badge that waits for `work-orders.json` is a badge that pops in half a
// second after the nav does, shifting every row below it. So the counts are
// split in two:
//
//   - {@link OEM_STATIC_COUNTS} — derived at module scope from the eager
//     `data/core.ts`, the compiled seeds and the brand registry. Correct on
//     first paint, never changes, no fetch.
//   - three pinned constants — the approval queue, the job board and the lake
//     document count, each of which needs a lazy domain or a directory
//     listing that is not available when the sidebar first paints.
//
// Every static count is a real derivation, not a transcribed literal. The
// three pinned ones are named as fallbacks rather than passed off as
// computed, and `scripts/oem/check-oem-counts.mjs` asserts all three against
// what ships on every build — so a pinned number cannot quietly go stale.
//
// Transformation layer (layer 3) — pure, no React, no fetch, no clock.
// @see src/versions/v1/shared/oem-nav.ts — where these land
// =========================================================================

import { FLEET, MINES, MINE_ORDER } from '../data/core';
import { ALERTS } from './seed/alerts';
import { INCIDENTS } from './seed/incidents';
import { CONNECTORS } from './seed/connectors';
import { AGENTS } from './seed/agents';
import { LOOPS } from './seed/loops';
import { MODULES } from './seed/modules';
import { statusOf } from './oem-engine';

/** Bands that put a machine on the watch list — the demo's `ATTN`. */
const ATTENTION: readonly string[] = ['poor', 'vpoor', 'major'];

/** Bands the S.O.S report treats as a hotspot. */
const HOTSPOT: readonly string[] = ['vpoor', 'major'];

/**
 * Customer organisations — one per mine operation.
 *
 * Deliberately **not** `new Set(FLEET.operated_by)`, which returns 7: that
 * field names whoever runs the machine, and three of its values (Redgum
 * Mining Services, Spinifex Underground Contracting, Western Tiers Mining
 * Services) are contracting firms working *on* a customer's site under that
 * customer's contract. They are service providers, not accounts. Meridian
 * sells to the six mine operations, which is why the badge reads 6.
 */
const CUSTOMER_ORGS: readonly string[] = MINE_ORDER.map((code) => MINES[code].name);

/** Distinct organisations that physically operate machines, customers plus contractors. */
const OPERATING_ORGS = new Set(FLEET.map((f) => f.operated_by)).size;

/** Machines in `poor`, `vpoor` or `major` — the fleet-health watch list. */
const MACHINES_TO_WATCH = FLEET.filter((f) => ATTENTION.includes(statusOf(f.machine_id))).length;

/** Machines in `vpoor` or `major` — the S.O.S hotspots. */
const SOS_HOTSPOTS = FLEET.filter((f) => HOTSPOT.includes(statusOf(f.machine_id))).length;

/**
 * Items in the approval queue: drafted work orders, agent-touched parts
 * orders, and the one standing restriction.
 *
 * The derivation needs `work-orders.json` and `parts.json`, both lazy, and
 * the sidebar renders before either lands — so this is a **pinned constant of
 * the frozen data pack**, not a guess. It is 12 drafted work orders plus 11
 * agent parts orders plus the restriction, which is exactly what
 * `approvalQueue()` returns and exactly what `/approvals` counts. The data is
 * frozen at the 09 Sep 2026 snapshot, so the two cannot drift without the
 * extractor being re-run.
 *
 * `scripts/oem/check-oem-counts.mjs` re-derives it from the raw JSON on every
 * build and fails if the two have parted.
 */
const APPROVALS_FALLBACK = 24;

/**
 * Open jobs on the board. Same story as {@link APPROVALS_FALLBACK}: `jobs()`
 * needs work orders, technician jobs and cases, all three lazy.
 *
 * `jobs()` is a long ordered de-duplication, so the check script does not
 * restate it — it bundles the engine and calls `jobs(ctx).length`, which is
 * the number `/job-board` itself renders.
 */
const JOB_BOARD_FALLBACK = 44;

/**
 * Lake documents in the data browser: every `public/data/oem/lake/*.json`
 * except `index.json`, which is the manifest over them rather than one of
 * them. A directory listing is not something the browser can do, so this is
 * pinned and the check script counts the files.
 */
const LAKE_DOCUMENTS = 271;

/**
 * Every badge the sidebar can show, keyed by the nav item id it belongs to.
 *
 * A nav row opts in by naming its key; a row with no entry renders no pill,
 * which is the right default for a landing page or a help article.
 */
export interface OemCounts {
  readonly customers: number;
  readonly 'job-board': number;
  readonly 'fleet-health': number;
  readonly 'sos-report': number;
  readonly approvals: number;
  readonly 'issues-live': number;
  readonly alerts: number;
  readonly incidents: number;
  readonly data: number;
  readonly connectors: number;
  readonly agents: number;
  readonly 'division-surface': number;
  readonly 'division-drilling': number;
  readonly 'division-loadhaul': number;
}

/** Machines in one division — the three asset-division badges. */
const inDivision = (division: string): number =>
  FLEET.filter((f) => f.division === division).length;

/**
 * The badges, computed once at module scope from eager data.
 *
 * Safe to read during the first render: nothing here fetches, and nothing
 * here reads the wall clock.
 */
export const OEM_STATIC_COUNTS: OemCounts = {
  customers: CUSTOMER_ORGS.length,
  'job-board': JOB_BOARD_FALLBACK,
  'fleet-health': MACHINES_TO_WATCH,
  'sos-report': SOS_HOTSPOTS,
  approvals: APPROVALS_FALLBACK,
  // The demo's "live issues" count is every alert plus every incident.
  'issues-live': ALERTS.length + INCIDENTS.length,
  alerts: ALERTS.length,
  incidents: INCIDENTS.length,
  data: LAKE_DOCUMENTS,
  connectors: CONNECTORS.length,
  agents: AGENTS.length,
  'division-surface': inDivision('Surface Drilling'),
  'division-drilling': inDivision('Underground Drilling'),
  'division-loadhaul': inDivision('Load & Haul'),
};

/** Nav item ids that carry a count. */
export type OemCountKey = keyof OemCounts;

/**
 * The only rows allowed a count pill in executive mode — fork(P7a/A4).
 *
 * Eleven numbers in the rail compete with the numbers on the page, and the
 * page is where the argument is made. These four earn their pill because each
 * one is a queue with a cost of waiting attached; Fleet health, Customers,
 * Job board and S.O.S are inventory counts, and an executive does not act on
 * them from the rail. Full mode is unchanged and still shows all eleven.
 */
export const EXECUTIVE_BADGE_KEYS: readonly OemCountKey[] = [
  'issues-live',
  'alerts',
  'incidents',
  'approvals',
];

/** Does this nav id keep its count pill when the rail is cut to the executive set? */
export const showsExecutiveBadge = (navItemId: string): boolean =>
  (EXECUTIVE_BADGE_KEYS as readonly string[]).includes(navItemId);

/** Narrowing guard — is this nav item id one the counts know about? */
export const hasCount = (navItemId: string): navItemId is OemCountKey =>
  Object.prototype.hasOwnProperty.call(OEM_STATIC_COUNTS, navItemId);

// ---------------------------------------------------------------------------
// Sidebar header
// ---------------------------------------------------------------------------

/**
 * The fleet header block over the nav — derived, so it tracks the data pack
 * rather than restating it.
 *
 * P2 left these as literals with a `TODO(p1-merge)`. The mine list is in
 * `MINE_ORDER` order, which is the demo's own site order everywhere else on
 * screen; sorting it alphabetically here would put the sidebar out of step
 * with the sites map and the cost table.
 */
export const FLEET_HEADER = {
  /**
   * fork(P7a/A5): `eyebrow` and `mines` are no longer rendered — the rail
   * shows `title` alone. Both stay derived here because they are the same two
   * facts other surfaces quote, and re-deriving them elsewhere is how a fork
   * ends up with two mine orders.
   */
  eyebrow: 'Aftermarket',
  title: `${FLEET.length} machines · ${MINE_ORDER.length} mines`,
  mines: CUSTOMER_ORGS.join(' · '),
  /** Customers plus the three contracting firms working on their sites. */
  organisations: OPERATING_ORGS,
} as const;

/** Counts quoted in the Asset360 hero and the platform footer line. */
export const PLATFORM_COUNTS = {
  modules: MODULES.length,
  loops: LOOPS.length,
  agents: AGENTS.length,
  connectors: CONNECTORS.length,
  machines: FLEET.length,
  mines: MINE_ORDER.length,
  alerts: ALERTS.length,
  incidents: INCIDENTS.length,
  customers: CUSTOMER_ORGS.length,
} as const;
